<template>
  <v-dialog :value="value" width="450" @click:outside="close">
    <v-card>
      <v-card-title>
        Move Selected Items
      </v-card-title>
      <v-card-text>
        Select an item group to move the selected items to.
        <v-autocomplete
          v-model="selectedItemGroup"
          label="Item Groups"
          :items="groupSelections"
          item-text="name"
          item-value="id"
          clearable>
        </v-autocomplete>
        <v-form v-if="selectedItemGroup === 'new_group'" ref="form" lazy-validation>
          <v-row dense class="mt-2">
          <v-col cols="12">
            <v-text-field
              v-model="newGroup.name"
              label="New Group Name"
              :rules="[v => !!v || 'Group name is required']"
              dense
              validate-on-blur
              outlined>
            </v-text-field>
          </v-col>
          <v-col
            v-for="(field, i) in currentFields"
            :key="i"
            cols="12">
            <v-checkbox
              v-if="field.isBoolean"
              v-model="newGroup[`${field.value}`]"
              :label="field.name"
              hide-details="auto"
              class="my-auto"
              dense
              @change="getItemAllowance(newGroup)">
            </v-checkbox>
            <v-text-field
              v-else-if="field.isInteger"
              v-model="newGroup[`${field.value}`]"
              :label="field.name"
              type="number"
              min="0"
              step="1"
              :suffix="field.suffix"
              background-color="#fff"
              dense
              autocomplete="off"
              outlined>
            </v-text-field>
            <v-text-field
              v-else-if="field.value === 'complex_promo_price'"
              v-model="newGroup[`${field.value}`]"
              :label="field.name"
              :disabled="!showComplexPromoPrice"
              type="number"
              min="0"
              step=".01"
              prefix="$"
              :rules="complexPromoPriceRules"
              :error="showErrorState(field, newGroup)"
              background-color="#fff"
              dense
              autocomplete="off"
              outlined>
            </v-text-field>
            <v-text-field
              v-else-if="field.isPercent"
              v-model="newGroup[`${field.value}`]"
              :label="field.name"
              type="number"
              min="0"
              step="1"
              suffix="%"
              :rules="[isValidPercent]"
              background-color="#fff"
              dense
              autocomplete="off"
              outlined>
            </v-text-field>
            <v-text-field
              v-else
              v-model="newGroup[`${field.value}`]"
              :label="field.name"
              :disabled="field.value === 'promotion_price' && disablePromotionPrice"
              type="number"
              min="0"
              step=".01"
              prefix="$"
              :rules="field.value === 'promotion_price' ? promoPriceRules : getPriceRules(field)"
              :error="showErrorState(field, newGroup)"
              background-color="#fff"
              dense
              autocomplete="off"
              outlined>
            </v-text-field>
          </v-col>
          <v-col v-if="isAccrualPromo" cols="12">
            <v-select
              v-model="newGroup.allowance_amt_type"
              label="Allowance Type"
              :items="accrualAllowanceTypes"
              :rules="[(v) => !!v || 'Please select an allowance type']"
              :menu-props="{ offsetY: true }"
              outlined
              dense>
            </v-select>
          </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-btn
          small
          text
          color="grey darken-2"
          @click="close">
          Close
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          small
          :disabled="selectedItemGroup === null"
          color="primary"
          @click="moveItems">
          Move Items
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
// Mixins
import { formValidation } from '@/mixins/form-validation'
import { displayAlert } from '@/mixins/alert'
import { utils } from '@/mixins/utils'
import { userAccess } from '@/mixins/user-access'

export default {
  name: 'MoveItemsDialog',
  mixins: [formValidation, utils, userAccess, displayAlert],
  data() {
    return {
      selectedItemGroup: null,
      newGroup: {
        name: 'New Group',
        buy_item: true,
        get_item: false,
        qty: 1,
        retail_price: null,
        amount_off: null,
        percent_off: null,
        promotion_price: null,
        complex_promo_price: null,
        rebate_amount: null,
        variance: null,
        accrual_amount: null,
        allowance_amt_type: null
      },
      accrualAllowanceTypes: ['Case', 'Percent', 'Per Pound'],
      billbackError: false
    }
  },
  props: {
    value: Boolean,
    selectedItems: Array,
    itemGroups: Array,
    promo_type_fields: Array,
    promo_type_constant: String,
    promo: Object,
  },
  computed: {
    groupSelections() {
      return [...this.itemGroups, { name: "+ Create New Group", id: 'new_group' }]
    },
    isComplexPromo() {
      return ['BUY_X_GET_X', 'BUY_X_SAVE_$Y'].includes(this.promo_type_constant)
    },
    disablePromotionPrice () {
      if (this.$auth.tenant === 'alliance-retail-group' ) return !this.isComplexPromo
      return this.overrideGroupValues
    },
    showComplexPromoPrice() {
      return this.isComplexPromo && this.newGroup.get_item
    },
    isAccrualPromo() {
      return this.promo_type_constant === 'ACCUMULATED_SAVINGS'
    },
    hideRedemptionAllowance() {
      return this.tenant === 'pricechopper' && this.promo_type_constant === 'BUY_X_GET_X'
    },
    isAMAP() {
      return this.promo?.promo_category === 'AMAP'
    },
    isEBA() {
      return this.promo?.promo_category === 'EBA'
    },
    promoPriceRules () {
      return (this.isAccrualPromo)
        ? [this.isValidRequiredPrice]
        : [this.isValidPrice]
    },
    complexPromoPriceRules() {
      return [v => (v !== null && v !== '') || 'Complex promo price is required']
    },
    isGetItemGroup () {
      return this.promo_type_constant === 'BUY_X_GET_X' && (this.newGroup.get_item && !this.newGroup.buy_item)
    },
    overrideGroupValues () {
      return this.isGetItemGroup && (this.$auth.tenant !== 'pricechopper')
    },
    billbackAllowanceField () {
      if (this.isAccrualPromo) {
        const allowanceType = this.newGroup.allowance_amt_type
        return {
          name: 'Billback Allowance',
          value: 'billback_allowance',
          isPercent: (allowanceType?.toLowerCase() === 'percent'),
          isInteger: (allowanceType?.toLowerCase() === 'per pound'),
          suffix: (allowanceType?.toLowerCase() === 'per pound') ? 'lbs' : undefined
        }
      }
      return { name: 'Billback Allowance', value: 'billback_allowance' }
    },
    checkItemGroupQuantities () {
      return this.$auth.tenant === 'alliance-retail-group' && this.promo_type_constant === 'BUY_X_SAVE_$Y'
    },
    otherItemGroups () {
      return this.itemGroups.filter(ig => ig?.id !== this.itemGroup?.id)
    },
    fields() {
      return [
        { name: 'Buy Item', value: 'buy_item', isBoolean: true, show: true },
        { name: 'Get Item', value: 'get_item', isBoolean: true, show: true },
        { name: 'Quantity', value: 'qty', isInteger: true, show: true },
        { name: 'Regular Retail', value: 'retail_price', show: true },
        { name: 'Percent Off', value: 'percent_off', isPercent: true, show: true },
        { 
          name: (this.isComplexPromo && this.$auth.tenant === 'alliance-retail-group') ? 'Base Ad Promo Price' : 'Promotion Price', 
          value: 'promotion_price', 
          show: true 
        },
        { 
          name: 'Complex Promo Price', 
          value: 'complex_promo_price', 
          show: this.showComplexPromoPrice 
        },
        { 
          name: this.isComplexPromo ? this.complexAllowanceLabel : 'Scan Allowance', 
          value: 'rebate_amount', 
          show: !this.hideRedemptionAllowance 
        },
        { name: 'Variance', value: 'variance', show: true },
        { name: 'AMAP Allowance', value: 'amap_allowance', show: true },
        { 
          name: 'Early Buy Allowance', 
          value: 'early_buy_allowance', 
          show: this.tenant === 'pricechopper' && this.isEBA 
        }
      ].filter(field => field.show)
    },
    currentFields () {
      if (this.promo_type_fields?.length > 0) {
        const fields = this.fields.filter(field => {
          if (field.value === 'variance' && this.tenant === 'awg') {
            return false // exclude the variance field for AWG tenant
          }
          if (field.value === 'promotion_price' && this.disablePromotionPrice) {
            return false
          }
          if (this.promo_type_fields.includes(field.value)) {
            if (field.value === 'amap_allowance') {
              return this.isAMAP
            }
            return true
          }
          return false
        })
        if (this.promo_type_fields.includes('billback_allowance')) {
          fields.push(this.billbackAllowanceField)
        }
        return fields
      }
      return []
    },
  },
  methods: {
    close() {
      this.$emit('close')
    },
    moveItems() {
      this.newGroup.promo_id = this.promo.id
      if (this.selectedItemGroup !== 'new_group') {
        const selectedGroup = this.itemGroups.find(ig => ig.id === this.selectedItemGroup)
        this.newGroup = { ...this.newGroup, ...selectedGroup }
      } else {
        const validGroup = this.validateGroup(this.newGroup)
        if (!validGroup) return
      }
      this.$emit('moveItems', this.selectedItemGroup, this.newGroup)
    },
    validateGroup (group) {
      if (this.isAccrualPromo) {
        return this.isValidAccrual(group)
      }
      const validFields = this.$refs.form?.validate()
      if (!this.validPromoLevel) {
        const errors = []
        if (!this.hasScanAllowance(group) && !this.validScanPromo) {
          this.scanError = true
          errors.push('Scan allowance is required.')
        }
        if (!this.hasBillingAllowance(group) && !this.validBillbackPromo) {
          this.billbackError = true
          errors.push(this.billbackErrorMessage)
        }
        if (errors.length > 0) {
          this.emitAlert(true, 'warning', null, errors)
          return false
        }
      }
      if (!validFields) {
        this.emitAlert(true, 'warning', 'Please check your form for errors')
        return false
      }
      if (this.checkItemGroupQuantities && this.otherItemGroups.some(ig => Number(ig.qty) !== Number(this.newGroup.qty))) {
        this.complexQuantityError = true
        this.emitAlert(true, 'warning', 'Quantity must be the same across all promo item groups for buy/save promos.')
        return false
      }
      return true
    },
    isValidAccrual (group) {
      const validFields = this.$refs.form.validate()
      const numericAllowance = this.getNumericPrice(group.billback_allowance)
      if (numericAllowance === 0) {
        this.billbackError = true
        this.emitAlert(true, 'warning', 'Billback allowance must have a value')
        return false
      }
      if (!validFields) {
        this.emitAlert(true, 'warning', 'Please check your form for errors')
        return false
      }
      return true
    },
    setGetItemAllowance (group) {
      if (this.overrideGroupValues) group.rebate_amount = '0.00'
    },
  }
}
</script>